<script setup>
import { onMounted, watch } from "vue";
import CustomCheckbox from "@/components/CustomCheckbox.vue";
import InputField from "@/components/forms/InputField.vue";
import RadioInput from "@/components/forms/RadioInput.vue";
import RadioInputGroup from "@/components/forms/RadioInputGroup.vue";
import NamedPriceList from "@/components/forms/NamedPriceList.vue";

import { PRICING_TICKET_TYPES, usePricingStore } from "../stores/pricingStore";
import { getPluralFromEventType } from "./methods/getPluralFromEventType";

const props = defineProps({
    eventType: String,
    recurringType: String,
    allEventsAmount: {
        type: Number,
        default: 0,
    },
});

const pricingStore = usePricingStore();

const toggleCheckbox = () => {
    pricingStore.sellingTicketAgreement = !pricingStore.sellingTicketAgreement;
};

const togglePricingOption = (option) => {
    pricingStore.togglePricingOption(option);
};

const toggleProRata = (type) => {
    pricingStore.prices[type].proRata = !pricingStore.prices[type].proRata;
};

onMounted(() => {
    if (props.recurringType !== undefined && props.recurringType !== null) {
        pricingStore.setRecurringType(props.recurringType);
    }
});

watch(() => props.recurringType, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        pricingStore.setRecurringType(newVal);
    }
});

watch(() => pricingStore.prices.single.namedPriceList, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        pricingStore.resetError();
    }
});

watch(() => pricingStore.prices.daily.namedPriceList, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        pricingStore.resetError();
    }
});

watch(() => pricingStore.prices.weekly.namedPriceList, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        pricingStore.resetError();
    }
});
</script>

<template>
    <div>
        <h2 class="">Tickets and price</h2>
        <!--  Selling ticket on chooser start  -->
        <section>
            <div class="mb-2">
                <label class="label" for="sellingTicketYes">
                    Would you like to sell tickets on Loopla?
                    <sup class="required-field">*</sup>
                </label>
                <div class="sub-label text-light-gray">
                    Loopla’s commission is 4.99% of your ticket revenue.
                </div>
            </div>

            <div
                class="d-flex flex-column single-radio-element"
            >
                <RadioInput
                    name="sellingTicketYes"
                    id="sellingTicketYes"
                    data-test="sellingTicketYes"
                    @update:modelValue="(value) => pricingStore.setSellingTicket(value)"
                    :value="true"
                    :selected="pricingStore.sellingTicket"
                >
                    Yes
                </RadioInput>
            </div>
            <div class="selling-ticket-agreement" v-if="pricingStore.sellingTicket">
                <CustomCheckbox @checkbox-click="toggleCheckbox" :checked="pricingStore.sellingTicketAgreement"
                                dataTest="sellingTicketAgreement">
                    I accept Loopla’s <a target="_blank" class="link"
                                         href="https://loopla.com/terms-of-use-for-business-users">business
                        terms
                        and conditions</a>.
                </CustomCheckbox>
                <div v-if="pricingStore.errors?.sellingTicketAgreement">
                    <div class="error-block mt-1" data-test="validation-error">{{
                        pricingStore.errors?.sellingTicketAgreement
                    }}
                    </div>
                </div>
            </div>
            <div
                class="d-flex flex-column single-radio-element"
            >
                <RadioInput
                    name="sellingTicketNo"
                    id="sellingTicketNo"
                    data-test="sellingTicketNo"
                    @update:modelValue="(value) => pricingStore.setSellingTicket(value)"
                    :value="false"
                    :selected="pricingStore.sellingTicket === false"
                >
                    No
                </RadioInput>
            </div>
            <div v-if="pricingStore.errors?.sellingTicket">
                <div class="error-block" data-test="validation-error">{{ pricingStore.errors?.sellingTicket }}</div>
            </div>
        </section>
        <!--  Selling ticket on chooser end  -->

        <!--  Available spaces start  -->
        <section v-if="pricingStore.sellingTicket && pricingStore.sellingTicketAgreement">
            <div class="top-gap mt-5">
                <InputField
                    inputClass="w-300"
                    :labelText="`Spaces available at each ${eventType?.toLowerCase()}`"
                    labelClass="mb-0"
                    subLabelText="Loopla will sell tickets on your behalf until there are no available spaces left. You can update the number of available spaces at any time."
                    v-model="pricingStore.spaceAvailable"
                    :value="pricingStore.spaceAvailable"
                    :error="pricingStore.errors?.spaceAvailable"
                    required
                    type="number"
                    min="0"
                    max="1000"
                />
            </div>
        </section>
        <!--  Available spaces end  -->

        <section
            v-if="pricingStore.sellingTicket === false
                || (pricingStore.sellingTicket && pricingStore.sellingTicketAgreement)">

            <!-- Event is free start -->
            <section>
                <label class="top-gap mb-2 mt-5 text-12" for="isFree">
                    Is your {{ eventType?.toLowerCase() }} free?<sup class="required-field">*</sup>
                </label>
                <div
                    class="d-flex flex-column single-radio-element"
                >
                    <RadioInput
                        name="isFreeYes"
                        id="isFreeYes"
                        data-test="isFreeYes"
                        @update:modelValue="(value) => pricingStore.setIsFree(value)"
                        :value="true"
                        :selected="pricingStore.isFree === true"
                    >
                        Yes
                    </RadioInput>
                </div>
                <div
                    class="d-flex flex-column single-radio-element"
                >
                    <RadioInput
                        name="isFreeNo"
                        id="isFreeNo"
                        data-test="isFreeNo"
                        @update:modelValue="(value) => pricingStore.setIsFree(value)"
                        :value="false"
                        :selected="pricingStore.isFree === false"
                    >
                        No
                    </RadioInput>
                </div>
                <div v-if="pricingStore.errors?.isFree">
                    <div class="error-block" data-test="validation-error">{{ pricingStore.errors?.isFree }}</div>
                </div>
            </section>
            <!-- Event is free end -->

            <!-- Not recurring event single pricing start -->
            <section v-if="pricingStore.isFree === false && pricingStore.recurringType === undefined">
                <div class="top-gap mb-3 mt-5">
                    <NamedPriceList
                        v-model:basePrice="pricingStore.prices.single.basePrice"
                        v-model:hasNamedPriceList="pricingStore.prices.single.hasNamedPriceList"
                        v-model:namedPriceList="pricingStore.prices.single.namedPriceList"
                        :base-price-error="pricingStore.errors?.oneOffBasePrice"
                        :named-price-error="pricingStore.errors?.oneOffNamedPriceList"
                        id="base-price"
                    />
                </div>
            </section>
            <!-- Not recurring event single pricing end -->

            <!-- Recurring event pricing start -->
            <section v-if="pricingStore.isFree === false && pricingStore.recurringType !==
            undefined">
                <p class="top-gap mb-2 label mt-5">
                    <label for="" class="text-12">Pricing options<sup class="required-field">*</sup></label>
                    <br/><span class="sub-label text-light-gray">Select as many options as you’d
                        like.</span>
                <div v-if="pricingStore.errors?.recurringOptions">
                    <div class="error-block mt-2" data-test="validation-error">{{
                        pricingStore.errors?.recurringOptions
                    }}
                    </div>
                </div>
                </p>
                <div class="d-flex flex-column">
                    <!-- 1.option Allow customers to attend a taster [event type] start -->
                    <div>
                        <CustomCheckbox
                            :checked="pricingStore.prices.taster.isActive"
                            @checkbox-click="togglePricingOption('taster')"
                            dataTest="price-free"
                        >
                            Allow customers to attend a taster {{ eventType?.toLowerCase() }}
                        </CustomCheckbox>
                        <div v-if="pricingStore.prices.taster.isActive" class="active-advanced-price-option">
                            <RadioInputGroup
                                label="title"
                                id="isFree"
                                dataTest="isFree"
                                v-model="pricingStore.prices.taster.type"
                                :selected="pricingStore.prices.taster.type"
                                :list="[
                                    {
                                        title: `Free trial ${eventType?.toLowerCase()}`,
                                        value: 'free',
                                    },
                                    {
                                        title: `Paid taster ${eventType?.toLowerCase()}`,
                                        value: 'paid',
                                    }
                                ]"
                            />
                            <div v-if="pricingStore.prices.taster.type === 'paid'">
                                <NamedPriceList
                                    v-model:basePrice="pricingStore.prices.taster.paidPrice"
                                    :namedPricingAllowed="false"
                                    :base-price-error="pricingStore.errors?.tasterPaidPrice"
                                    id="paid-taster-price"
                                />
                            </div>
                        </div>
                    </div>
                    <!-- 1.option Allow customers to attend a taster [event type] end -->

                    <!-- 2.[SINGLE] option Allow customers to buy single tickets to any number of [event type] start -->
                    <div>
                        <CustomCheckbox
                            :checked="pricingStore.prices.single.isActive"
                            @checkbox-click="() => togglePricingOption(PRICING_TICKET_TYPES.SINGLE)"
                            dataTest="price-free"
                        >
                            {{
                                pricingStore.sellingTicket ?
                                    `Allow customers to buy single tickets
                            to any number of ${ getPluralFromEventType(eventType) }`
                                    : `Set a price per ${eventType?.toLowerCase()}`
                            }}

                        </CustomCheckbox>
                        <div v-if="pricingStore.prices.single.isActive" class="active-advanced-price-option">
                            <NamedPriceList
                                v-model:basePrice="pricingStore.prices.single.basePrice"
                                v-model:hasNamedPriceList="pricingStore.prices.single.hasNamedPriceList"
                                v-model:namedPriceList="pricingStore.prices.single.namedPriceList"
                                :base-price-error="pricingStore.errors?.singleBasePrice"
                                :named-price-error="pricingStore.errors?.singleNamedPriceList"
                                id="single-price"
                            />
                        </div>
                    </div>
                    <!-- 2.option Allow customers to buy single tickets to any number of [event type] end -->

                    <!-- 3.[DAILY]option Allow customers to purchase a [X] day ticket (aka. the entire term or course) start -->
                    <div v-if="pricingStore.recurringType === PRICING_TICKET_TYPES.DAILY">
                        <CustomCheckbox
                            :checked="pricingStore.prices.daily.isActive"
                            @checkbox-click="() => togglePricingOption(PRICING_TICKET_TYPES.DAILY)"
                            dataTest="price-free"
                        >
                            {{
                                pricingStore.sellingTicket ?
                                    `Allow customers to purchase a ${ allEventsAmount }
                                    day ticket (aka. the entire term or course)`
                                    : `Set a price for all ${ allEventsAmount } ${
                                        getPluralFromEventType(eventType) }
                                        (aka. the entire term or course)`
                            }}
                        </CustomCheckbox>
                        <div v-if="pricingStore.prices.daily.isActive" class="active-advanced-price-option">
                            <NamedPriceList
                                v-model:basePrice="pricingStore.prices.daily.basePrice"
                                v-model:hasNamedPriceList="pricingStore.prices.daily.hasNamedPriceList"
                                v-model:namedPriceList="pricingStore.prices.daily.namedPriceList"
                                :base-price-error="pricingStore.errors?.dailyBasePrice"
                                :named-price-error="pricingStore.errors?.dailyNamedPriceList"
                                id="daily-price"
                            />
                            <CustomCheckbox
                                @checkbox-click="() => toggleProRata(PRICING_TICKET_TYPES.DAILY)"
                                :checked="pricingStore.prices.daily.proRata" dataTest="price-free">
                                Automatically lower this price
                                depending on how many {{ getPluralFromEventType(eventType) }}
                                are left (pro-rata)
                            </CustomCheckbox>
                        </div>
                    </div>
                    <!-- 3.option Allow customers to purchase a [X] day ticket (aka. the entire term or course) end -->

                    <!-- 4.[WEEKLY] option Allow customers to purchase a [X] week ticket (aka. the entire term or course) start -->
                    <div v-if="pricingStore.recurringType === PRICING_TICKET_TYPES.WEEKLY">
                        <CustomCheckbox
                            :checked="pricingStore.prices.weekly.isActive"
                            @checkbox-click="() => togglePricingOption(PRICING_TICKET_TYPES.WEEKLY)"
                            dataTest="price-free"
                        >
                            {{
                                pricingStore.sellingTicket ?
                                    `Allow customers to purchase a ${ allEventsAmount }
                                    week ticket (aka. the entire term or course)`
                                    : `Set a price for all ${ allEventsAmount }
                                    ${ getPluralFromEventType(eventType) }
                                        (aka. the entire term or course)`
                            }}
                        </CustomCheckbox>
                        <div v-if="pricingStore.prices.weekly.isActive" class="active-advanced-price-option">
                            <NamedPriceList
                                v-model:basePrice="pricingStore.prices.weekly.basePrice"
                                v-model:hasNamedPriceList="pricingStore.prices.weekly.hasNamedPriceList"
                                v-model:namedPriceList="pricingStore.prices.weekly.namedPriceList"
                                :base-price-error="pricingStore.errors?.weeklyBasePrice"
                                :named-price-error="pricingStore.errors?.weeklyNamedPriceList"
                                id="weekly-price"
                            />
                            <CustomCheckbox
                                @checkbox-click="() => toggleProRata(PRICING_TICKET_TYPES.WEEKLY)"
                                :checked="pricingStore.prices.weekly.proRata" dataTest="price-free">
                                Automatically lower this price depending
                                on how many {{ getPluralFromEventType(eventType) }}
                                are left (pro-rata)
                            </CustomCheckbox>
                        </div>
                    </div>
                    <!-- 3.option Allow customers to purchase a [X] week ticket (aka. the entire term or course) end -->
                </div>
            </section>
            <!-- Recurring event pricing end -->
        </section>
    </div>
</template>

<style scoped>
h2 {
    font-style: normal;
    font-weight: 600;
    font-size: 2rem;
    line-height: 48px;
    margin-bottom: 2.6rem;
}

label, .label {
    font-weight: 600;
    visibility: visible !important;
    opacity: 1 !important;
    font-size: 14px;
}

.sub-label {
    font-style: italic;
    font-weight: 400;
    font-size: 0.881rem;
    line-height: 1.381rem;
    color: #636066;
    margin-bottom: 8px;
}

.payment-box {
    border-radius: 5px;
    background: var(--Very-Light-Grey, #F7F7F7);
    padding: 16px;
    margin-top: 8px;
    margin-bottom: 16px;
}

.single-radio-element {
    margin-bottom: 0.4rem;
    margin-right: 1rem;
    width: max-content;
}

.selling-ticket-agreement {
    background: var(--Very-Light-Grey, #F7F7F7);
    border-radius: 5px;
    margin-bottom: .7rem;
    padding: .2rem .5rem .5rem 1rem;
}

.active-advanced-price-option {
    margin-top: .2rem;
    background: var(--Very-Light-Grey, #F7F7F7);
    border-radius: 5px;
    margin-bottom: .7rem;
    padding: 1.5rem;
}

.card-element {
    min-width: 700px;
    min-height: 111px;
    background: #ffffff;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
    border-radius: 5px;
    padding: 16px;
    margin-top: 21px;
    margin-bottom: 36px;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.card-element-icon {
    align-self: center;
    margin-right: 12px;
}

.card-element-text {
    font-size: 14px;
    line-height: 21px;
    font-weight: 400;
}

.error-block {
    margin-top: 3px;
    color: #eb4247;
    font-weight: 400;
    font-size: 0.881rem;
    line-height: 1.381rem;
}

.text-12 {
    font-size: 12px;
}

.link{
    font-weight: normal;
}

.text-light-gray {
    color: rgba(153, 153, 153, 1)
}
</style>
